import React from 'react';
import styled, { css, FlattenSimpleInterpolation } from 'styled-components';
import { FontProps, FontWeights, getFontCss, getFontWeights, theme } from 'utils/theme';

type BodyTextProps = {
  margin?: string;
  size?: FlattenSimpleInterpolation;
  weight?: FlattenSimpleInterpolation;
  underline?: boolean;
  centered?: boolean;
  clickable?: boolean;
  light?: boolean;
  noWrap?: boolean;
} & FontProps;

const BodyText = styled.p<BodyTextProps>`
  ${(props) => getFontCss({ alternates: props.alternates })}
  font-weight: 300;
  font-size: 14px;
  line-height: 140%;
  margin: ${(props) => props.margin || '5px 0'};
  text-align: justify;

  ${(props) =>
    props.color &&
    css`
      color: ${props.theme.colors[props.color] || props.color};
    `}

  ${(props) => props.size}
  ${(props) => props.weight}
  
  ${(props) =>
    props.underline &&
    css`
      text-decoration: underline;
    `}
  
  ${(props) =>
    props.centered &&
    css`
      text-align: center;
    `}
  
  ${(props) => props.noWrap && 'white-space: nowrap;'}
  ${(props) => (props.clickable ? 'cursor: pointer;' : '')}
  ${(props) => (props.light && !props.color ? `color: ${theme.colors.white}` : '')}
`;

type BodyProps = {
  margin?: string;
  color?: string;
  size?: 'xsmall' | 'small' | 'regular';
  weight?: FontWeights;
  underline?: boolean;
  centered?: boolean;
  onClick?: (e?: React.MouseEvent) => void;
  children: React.ReactNode;
  light?: boolean;
  it?: boolean;
  noWrap?: boolean;
  fontStyle?: string;
} & FontProps;

const Body: React.FC<BodyProps> = ({
  children,
  margin,
  color,
  size = 'regular',
  weight = 'regular',
  underline,
  centered,
  light,
  onClick,
  it,
  alternates,
  noWrap,
  fontStyle,
}) => {
  const italicStyle = it
    ? css`
        font-style: ${fontStyle || 'italic'};
        border-left: 3px ridge black;
        padding-left: 8px;
      `
    : '';

  const sizes = {
    xsmall: css`
      font-size: 12px;
      line-height: 12px;
    `,
    small: css`
      font-size: 14px;
      line-height: 140%;
    `,
    regular: css`
      font-size: 16px;
      line-height: 150%;
    `,
  };

  return (
    <BodyText
      color={color}
      margin={margin}
      size={sizes[size]}
      weight={css`
        font-weight: ${getFontWeights(weight)};
        ${italicStyle}
      `}
      underline={underline}
      centered={centered}
      clickable={onClick !== undefined}
      onClick={onClick}
      light={light}
      alternates={alternates}
      noWrap={noWrap}
    >
      {children}
    </BodyText>
  );
};

export default Body;
